import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva } from "class-variance-authority"

import { cn } from "@/lib/utils"

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-[6px] text-sm font-semibold uppercase transition duration-300 ease-in-out disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive cursor-pointer",
  {
    variants: {
      variant: {
        default:
          "bg-primary text-primary-foreground shadow-xs hover:bg-accent-lilac hover:text-accent-lilac-foreground focus-visible:bg-accent-lilac focus-visible:text-accent-lilac-foreground",
        "default-outline":
          "border border-primary text-primary bg-background shadow-xs hover:bg-primary hover:text-primary-foreground focus-visible:ring-primary/20 dark:focus-visible:ring-primary/40 dark:border-primary dark:hover:bg-primary/90",
        destructive:
          "bg-destructive text-white shadow-xs hover:bg-destructive-700 focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40 dark:bg-destructive/60",
        "destructive-outline":
          "border border-destructive text-destructive bg-background shadow-xs hover:bg-destructive hover:text-white focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40 dark:border-destructive dark:hover:bg-destructive/90",
        warning:
          "bg-warning text-warning-foreground shadow-xs hover:bg-warning-700 focus-visible:ring-warning/20 dark:focus-visible:ring-warning/40",
        "warning-outline":
          "border border-warning text-warning-800 bg-background shadow-xs hover:bg-warning hover:text-warning-foreground focus-visible:ring-warning/20 dark:focus-visible:ring-warning/40 dark:border-warning dark:hover:bg-warning/90",
        outline:
          "border bg-background shadow-xs hover:bg-accent hover:text-accent-foreground dark:bg-input/30 dark:border-input dark:hover:bg-input/50",
        secondary:
          "bg-secondary text-secondary-foreground shadow-xs hover:bg-secondary-700",
        "secondary-outline":
          "border border-secondary text-secondary bg-background shadow-xs hover:bg-secondary hover:text-secondary-foreground focus-visible:ring-secondary/20 dark:focus-visible:ring-secondary/40 dark:border-secondary dark:hover:bg-secondary/90",
        tertiary:
          "bg-tertiary text-tertiary-foreground shadow-xs hover:bg-tertiary-700",
        "tertiary-outline":
          "border border-tertiary text-tertiary bg-background shadow-xs hover:bg-tertiary hover:text-tertiary-foreground focus-visible:ring-tertiary/20 dark:focus-visible:ring-tertiary/40 dark:border-tertiary dark:hover:bg-tertiary/90",
        lilac:
          "bg-accent-lilac text-black shadow-xs hover:bg-accent-lilac/80 focus-visible:ring-accent-lilac/40",
        ghost:
          "hover:bg-accent hover:text-accent-foreground dark:hover:bg-accent/50",
        link: "text-primary normal-case font-medium rounded-none !p-0 self-start underline underline-offset-4 hover:text-primary/80 hover:no-underline !text-[length:inherit]",
        "link-secondary":
          "text-foreground normal-case font-bold rounded-none !p-0 self-start bg-[linear-gradient(var(--color-accent-hot-pink),var(--color-accent-hot-pink))] bg-no-repeat [background-position:left_bottom] [background-size:100%_1px] hover:[background-size:0%_1px] [transition:background-size_300ms_ease-in-out]",
      },
      size: {
        default: "px-[21px] py-[14px] has-[>svg]:px-[17px]",
        sm: "h-8 rounded-[6px] gap-1.5 px-3 has-[>svg]:px-2.5",
        lg: "h-10 rounded-[6px] px-6 has-[>svg]:px-4",
        xl: "h-12 rounded-[6px] px-8 has-[>svg]:px-6 text-lg",
        icon: "size-9",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

const Button = React.forwardRef(({ className, variant, size, asChild = false, ...props }, ref) => {
  const Comp = asChild ? Slot : "button"

  return (
    <Comp
      ref={ref}
      data-slot="button"
      className={cn(buttonVariants({ variant, size: variant === "link" || variant === "link-secondary" ? undefined : size, className }))}
      {...props}
    />
  )
})

Button.displayName = "Button"

export { Button, buttonVariants }
